import type { MaybeRefOrGetter, Ref } from 'vue';
import { ref, toRef, toValue, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useLineItemsSummary } from '@/composables/useLineItemsSummary';
import { ProposedShipment } from '@/model/routeBuilder';
import { RoutePlanProposedShipment } from '@/model/routeBuilder/phase2';

export interface UseProposedShipmentValidationsResponse {
  checkForWarnings: () => void;
  errors: Ref<string[]>;
  validate: () => Promise<boolean>;
  warnings: Ref<string[]>;
}

export const useProposedShipmentValidations = (
  proposedShipment: MaybeRefOrGetter<
    ProposedShipment | RoutePlanProposedShipment
  >,
): UseProposedShipmentValidationsResponse => {
  const lineItems = toRef(() => toValue(proposedShipment).lineItems);

  const { t } = useI18n();
  const { totalHandlingUnits, totalLinear, totalVolume, totalWeight } =
    useLineItemsSummary(lineItems);

  const warnings: Ref<string[]> = ref([]);
  const errors: Ref<string[]> = ref([]);

  const stops = toRef(() => toValue(proposedShipment).stops);
  const condensedStops = toRef(() => {
    return stops.value.map((s) => ({
      sequenceNumber: s.sequenceNumber,
      shipOrderShippingPointIds: s.shipOrderShippingPointIds,
    }));
  });

  const validateDimensions = (): string[] => {
    const dimsErrors: string[] = [];

    if (totalWeight.value > ProposedShipment.MAX_WEIGHT_LBS) {
      dimsErrors.push(
        t('validations.routeBuilder.maxWeightExceeded', {
          totalWeight: totalWeight.value,
          maxWeight: ProposedShipment.MAX_WEIGHT_LBS,
        }),
      );
    }

    if (totalHandlingUnits.value > ProposedShipment.MAX_PALLET_COUNT) {
      dimsErrors.push(
        t('validations.routeBuilder.maxPalletExceeded', {
          totalPalletCount: totalHandlingUnits.value,
          maxPalletCount: ProposedShipment.MAX_PALLET_COUNT,
        }),
      );
    }

    if (totalLinear.value > ProposedShipment.MAX_LINEAR_FEET) {
      dimsErrors.push(
        t('validations.routeBuilder.maxLinearExceeded', {
          totalLinearFeet: totalLinear.value,
          maxLinearFeet: ProposedShipment.MAX_LINEAR_FEET,
        }),
      );
    }

    if (totalVolume.value > ProposedShipment.MAX_VOLUME_CUBIC_FEET) {
      dimsErrors.push(
        t('validations.routeBuilder.maxVolumeExceeded', {
          totalVolume: totalVolume.value,
          maxVolume: ProposedShipment.MAX_VOLUME_CUBIC_FEET,
        }),
      );
    }

    return dimsErrors;
  };

  const validateNumStops = (): string[] => {
    const rawShipment = toValue(proposedShipment);
    return rawShipment.stops.length < 2
      ? [t('validations.routeBuilder.missingStops')]
      : [];
  };

  const validateStopLocation = (): string[] => {
    const rawShipment = toValue(proposedShipment);
    return rawShipment.stops
      .filter((s) => s.isPlaceholderStop)
      .map((stop) =>
        t('validations.routeBuilder.missingLocationForSequenceNumber', {
          sequenceNumber: stop.sequenceNumber,
        }),
      );
  };

  const validateStopSequencing = (): string[] => {
    const rawShipment = toValue(proposedShipment);
    return rawShipment.stops
      .map((stop, _index, allStops) => {
        const stopErrors: string[] = [];

        // line items being picked up at this stop
        const lineItemsBeingPickedUp = lineItems.value.filter(
          (lineItem) =>
            lineItem.pickupStopSequenceNumber === stop.sequenceNumber,
        );

        // the drop-off stops for all the line items that are being picked up at this stop
        const relatedDropOffStops = lineItemsBeingPickedUp
          .map((lineItem) => lineItem.dropOffStopSequenceNumber)
          .filter((dropOffStop) => dropOffStop !== undefined);

        // the lowest sequence number of all stops that are dropoffs for this pickup
        // the stop being modified cannot be moved after this sequence number
        const maxSeqNum =
          relatedDropOffStops.length > 0
            ? Math.min(...relatedDropOffStops)
            : allStops.length + 1;

        if (stop.sequenceNumber >= maxSeqNum) {
          stopErrors.push(
            t('validations.routeBuilder.dropOffAfterPickup', {
              sequenceNumber: stop.sequenceNumber,
              maxSeqNum,
            }),
          );
        }

        // line items being dropped off at this stop
        const lineItemsBeingDroppedOff = lineItems.value.filter(
          (lineItem) =>
            lineItem.dropOffStopSequenceNumber === stop.sequenceNumber,
        );

        // the pick-up stops for all the line items that are being dropped off at this stop
        const relatedPickUpStops = lineItemsBeingDroppedOff
          .map((lineItem) => lineItem.pickupStopSequenceNumber)
          .filter((pickupStop) => pickupStop !== undefined);

        // the highest sequence number of all stops that are pickups for this dropoff
        // the stop being modified cannot be moved before this sequence number
        const minSeqNum =
          relatedPickUpStops.length > 0 ? Math.max(...relatedPickUpStops) : -1;

        if (stop.sequenceNumber <= minSeqNum) {
          stopErrors.push(
            t('validations.routeBuilder.pickupBeforeDropOff', {
              sequenceNumber: stop.sequenceNumber,
              minSeqNum,
            }),
          );
        }

        if (
          !lineItemsBeingPickedUp.length &&
          !lineItemsBeingDroppedOff.length
        ) {
          return [
            t('validations.routeBuilder.missingLineItemsForSequenceNumber', {
              sequenceNumber: stop.sequenceNumber,
            }),
          ];
        }

        return stopErrors;
      })
      .flat();
  };

  const checkForWarnings = () => {
    const dimsValidationErrors = validateDimensions();
    warnings.value = [...dimsValidationErrors];
  };

  const validate = async (): Promise<boolean> => {
    const numStopsErrors = validateNumStops();
    const stopLocationErrors = validateStopLocation();
    const stopSequencingErrors = validateStopSequencing();

    errors.value = [
      ...numStopsErrors,
      ...stopLocationErrors,
      ...stopSequencingErrors,
    ];
    return errors.value.length === 0;
  };

  const reset = () => {
    warnings.value = [];
    errors.value = [];
  };

  watch(
    condensedStops,
    () => {
      reset();
      checkForWarnings();
    },
    { deep: true, immediate: true },
  );

  watch(
    lineItems,
    () => {
      reset();
      checkForWarnings();
    },
    { deep: true, immediate: true },
  );

  return {
    checkForWarnings,
    errors,
    validate,
    warnings,
  };
};
